import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";
import { FormFooter } from "../FormFooter";

const switchVariants = cva(
  "relative inline-flex items-center shrink-0 cursor-pointer rounded-full transition-all duration-200",
  {
    variants: {
      size: {
        small: "w-[20px] h-[12px]",
        medium: "w-[28px] h-[16px]",
        large: "w-[36px] h-[20px]",
      },
      validationState: {
        none: "",
        error: "",
      },
      isChecked: {
        true: "",
        false: "",
      },
      isDisabled: {
        true: "cursor-not-allowed bg-surface-fill-neutral-moderate",
        false: "",
      },
      isFocused: {
        true: "",
        false: "",
      },
    },
    compoundVariants: [
      // Unchecked state - none validation
      {
        isChecked: false,
        validationState: "none",
        isDisabled: false,
        class:
          "bg-surface-fill-neutral-subtle hover:bg-action-fill-neutral-faded",
      },
      // Checked state - none validation
      {
        isChecked: true,
        validationState: "none",
        isDisabled: false,
        class:
          "bg-action-fill-primary-default hover:bg-action-fill-primary-hover",
      },
      // Checked state - error validation
      {
        isChecked: true,
        validationState: "error",
        isDisabled: false,
        class:
          "bg-action-fill-negative-default hover:bg-action-fill-negative-hover",
      },
      // Unchecked state - error validation
      {
        isChecked: false,
        validationState: "error",
        isDisabled: false,
        class:
          "bg-surface-fill-neutral-subtle hover:bg-action-fill-neutral-faded",
      },
      // Focused state - none validation
      {
        isFocused: true,
        validationState: "none",
        isDisabled: false,
        class: "ring-2 ring-action-outline-primary-faded",
      },
      // Focused state - error validation
      {
        isFocused: true,
        validationState: "error",
        isDisabled: false,
        class: "ring-2 ring-action-outline-negative-faded",
      },
    ],
    defaultVariants: {
      size: "medium",
      validationState: "none",
      isChecked: false,
      isDisabled: false,
      isFocused: false,
    },
  }
);

const switchThumbVariants = cva(
  "inline-block rounded-full bg-white shadow-sm transition-transform duration-200",
  {
    variants: {
      size: {
        small: "h-[8px] w-[8px]",
        medium: "h-[12px] w-[12px]",
        large: "h-[16px] w-[16px]",
      },
      isChecked: {
        true: "",
        false: "",
      },
    },
    compoundVariants: [
      // Small size translations
      {
        size: "small",
        isChecked: false,
        class: "translate-x-[2px]",
      },
      {
        size: "small",
        isChecked: true,
        class: "translate-x-[10px]",
      },
      // Medium size translations
      {
        size: "medium",
        isChecked: false,
        class: "translate-x-[2px]",
      },
      {
        size: "medium",
        isChecked: true,
        class: "translate-x-[14px]",
      },
      // Large size translations
      {
        size: "large",
        isChecked: false,
        class: "translate-x-[2px]",
      },
      {
        size: "large",
        isChecked: true,
        class: "translate-x-[18px]",
      },
    ],
    defaultVariants: {
      size: "medium",
      isChecked: false,
    },
  }
);

export interface SwitchProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  /**
   * Label text to display next to the switch
   */
  label?: string;
  /**
   * Error text to display below the switch
   */
  errorText?: string;
  /**
   * Size of the switch
   */
  size?: "small" | "medium" | "large";
  /**
   * Validation state of the switch
   */
  validationState?: "none" | "error";
  /**
   * Whether the switch is disabled
   */
  isDisabled?: boolean;
  /**
   * Whether to show error text (defaults to true if errorText is provided)
   */
  showErrorText?: boolean;
  /**
   * Custom class name for the container
   */
  containerClassName?: string;
  /**
   * Custom class name for the label
   */
  labelClassName?: string;
  /**
   * Custom class name for the switch track
   */
  trackClassName?: string;
  /**
   * Custom class name for the switch thumb
   */
  thumbClassName?: string;
}

const Switch = React.forwardRef<HTMLInputElement, SwitchProps>(
  (
    {
      label,
      errorText,
      size = "medium",
      validationState = "none",
      isDisabled = false,
      showErrorText = true,
      containerClassName,
      labelClassName,
      trackClassName,
      thumbClassName,
      className,
      checked,
      onChange,
      ...props
    },
    ref
  ) => {
    const [internalChecked, setInternalChecked] = React.useState(false);
    const [showRipple, setShowRipple] = React.useState(false);
    const [isFocused, setIsFocused] = React.useState(false);
    const inputRef = React.useRef<HTMLInputElement>(null);

    // Use forwarded ref or internal ref
    React.useImperativeHandle(ref, () => inputRef.current!);

    const isChecked = checked !== undefined ? checked : internalChecked;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(e);
      } else {
        setInternalChecked(e.target.checked);
      }
    };

    const triggerRipple = () => {
      if (!isDisabled) {
        setShowRipple(true);
        setTimeout(() => {
          setShowRipple(false);
        }, 360); // Match animation duration (0.36s)
      }
    };

    const handleContainerClick = () => {
      if (!isDisabled && inputRef.current) {
        triggerRipple();
        inputRef.current.click();
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if ((e.key === " " || e.key === "Enter") && !isDisabled) {
        e.preventDefault();
        triggerRipple();
        inputRef.current?.click();
      }
    };

    const handleFocus = () => {
      if (!isDisabled) {
        setIsFocused(true);
      }
    };

    const handleBlur = () => {
      setIsFocused(false);
    };

    // Size-based configurations
    const sizeConfig = {
      small: {
        gap: "gap-2",
        labelSize: "text-body-small-regular",
      },
      medium: {
        gap: "gap-2.5",
        labelSize: "text-body-small-regular",
      },
      large: {
        gap: "gap-3",
        labelSize: "text-body-medium-regular",
      },
    };

    const config = sizeConfig[size];

    // Determine if we should show the error text
    const shouldShowError = errorText && showErrorText;

    return (
      <div className={cn("inline-flex flex-col", containerClassName)}>
        <div
          className={cn(
            "inline-flex items-center",
            config.gap,
            isDisabled ? "cursor-not-allowed" : "cursor-pointer"
          )}
          onClick={handleContainerClick}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          role="switch"
          aria-checked={isChecked}
          aria-disabled={isDisabled}
          tabIndex={isDisabled ? -1 : 0}
        >
          <input
            ref={inputRef}
            type="checkbox"
            role="switch"
            className="sr-only"
            checked={isChecked}
            onChange={handleChange}
            disabled={isDisabled}
            {...props}
          />

          <div className="relative inline-flex shrink-0">
            {/* Ripple Effect */}
            {showRipple && (
              <div
                className={cn(
                  "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full pointer-events-none w-full h-full",
                  validationState === "error"
                    ? "bg-action-outline-negative-faded"
                    : "bg-action-outline-primary-faded"
                )}
                style={{
                  animation: "var(--animate-checkbox-ripple)",
                }}
              />
            )}

            <div
              className={cn(
                switchVariants({
                  size,
                  validationState,
                  isChecked,
                  isDisabled,
                  isFocused,
                }),
                trackClassName,
                className
              )}
            >
              <span
                className={cn(
                  switchThumbVariants({
                    size,
                    isChecked,
                  }),
                  thumbClassName
                )}
              />
            </div>
          </div>

          {label && (
            <label
              className={cn(
                config.labelSize,
                "select-none inline-flex items-center",
                isDisabled
                  ? "text-surface-ink-neutral-disabled"
                  : "text-surface-ink-neutral-normal",
                labelClassName
              )}
            >
              {label}
            </label>
          )}
        </div>

        {/* Error text */}
        {shouldShowError && (
          <FormFooter
            helperText={errorText}
            validationState="negative"
            size={size}
            isDisabled={isDisabled}
          />
        )}
      </div>
    );
  }
);

Switch.displayName = "Switch";

export { Switch, switchVariants };
